// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-5 §2.5 / D-POT-8 — "Send for signature" on the kit. The server's signing plan decides the paths
// (carerxPotSend.usePotSendFlow, shared with POT classic): when the physician has an OwlRx login they can sign in
// their provider portal ("Sign in OwlRx"); otherwise — or by choice under Both — DocuSign, prefilled from §14.
import Box from '@mui/material/Box'
import AppButton from '../../components/ui/AppButton'
import AppDialog from '../../components/ui/AppDialog'
import { AppTextField } from '../../components/ui/FormFields'
import Notice from '../../components/ui/Notice'
import { isValidEmail, type PotRow } from './carerxPotDashboardLogic'
import { usePotSendFlow } from './carerxPotSend'

export default function CareRxPotSendDialog({ row, onClose, onSent }: { row: PotRow; onClose: () => void; onSent: () => void }) {
  const f = usePotSendFlow(row)
  const { plan, words, phase, setPhase, name, setName, email, setEmail, result } = f
  const emailOk = isValidEmail(email)
  const title = phase === 'ok' ? (result.channel === 'owlrx' ? 'POT sent to the physician\'s OwlRx portal' : 'POT sent for signature')
    : phase === 'fail' ? 'Send failed' : 'Send for signature'
  return (
    <AppDialog open title={title} onClose={phase === 'sending' ? () => undefined : onClose} busy={phase === 'sending'} maxWidth="xs"
      actions={
        phase === 'choose' ? <>
          <AppButton onClick={onClose}>Cancel</AppButton>
          {plan?.docusign && <AppButton variant="outlined" onClick={() => setPhase('edit')}>Send with DocuSign instead</AppButton>}
          <AppButton variant="contained" onClick={() => setPhase('confirmInApp')}>Sign in OwlRx</AppButton>
        </>
          : phase === 'confirmInApp' ? <><AppButton onClick={() => setPhase('choose')}>Back</AppButton><AppButton variant="contained" onClick={() => { void f.sendInApp() }}>Send to {words?.physician}</AppButton></>
          : phase === 'edit' ? <>
            <AppButton onClick={plan?.in_app ? () => setPhase('choose') : onClose}>{plan?.in_app ? 'Back' : 'Cancel'}</AppButton>
            <AppButton variant="contained" disabled={!f.docuSignValid} onClick={() => setPhase('confirm')}>Continue</AppButton>
          </>
          : phase === 'confirm' ? <><AppButton onClick={() => setPhase('edit')}>Back</AppButton><AppButton variant="contained" onClick={() => { void f.sendDocuSign() }}>Send</AppButton></>
          : phase === 'ok' ? <AppButton variant="contained" onClick={onSent}>OK</AppButton>
          : phase === 'fail' || phase === 'blocked' ? <AppButton onClick={onClose}>Close</AppButton> : null
      }>
      {phase === 'loading' && <Box sx={{ color: 'text.secondary', fontSize: 13 }}>Loading physician details…</Box>}
      {(phase === 'choose' || phase === 'confirmInApp' || phase === 'edit') && words?.mismatch && <Notice severity="warning">{words.mismatch}</Notice>}
      {phase === 'choose' && words && (
        <Box sx={{ display: 'grid', gap: 1, pt: 0.5, fontSize: 13 }}>
          <b>{words.inAppTitle}</b>
          <Box sx={{ color: 'text.secondary', fontSize: 12.5 }}>{words.inAppBody}</Box>
        </Box>
      )}
      {phase === 'confirmInApp' && <Box sx={{ fontSize: 13 }}>Send this POT to <b>{words?.physician}</b>'s OwlRx portal for signature?<Box sx={{ mt: 1.5, color: 'text.secondary', fontSize: 12.5 }}>Once sent, the POT is locked and can only be revoked, not re-sent.</Box></Box>}
      {phase === 'edit' && (
        <Box sx={{ display: 'grid', gap: 1.5, pt: 0.5 }}>
          <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>The certifying physician receives the POT by email (DocuSign) for e-signature. Confirm the name and email:</Box>
          <AppTextField id="sigPhysName" label="Physician name" size="small" value={name} onChange={e => setName(e.target.value)} placeholder="Dr. " autoFocus fullWidth />
          <AppTextField id="sigPhysEmail" label="Physician email" size="small" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="physician@clinic.com" fullWidth
            error={email.length > 0 && !emailOk} helperText={email.length > 0 && !emailOk ? 'Enter a valid email address' : undefined} />
        </Box>
      )}
      {phase === 'confirm' && <Box sx={{ fontSize: 13 }}>Send this POT to <b>{name.trim()}</b> ({email.trim()}) for signature via DocuSign?<Box sx={{ mt: 1.5, color: 'text.secondary', fontSize: 12.5 }}>Once sent, the POT is locked and can only be revoked, not re-sent.</Box></Box>}
      {phase === 'sending' && <Box sx={{ textAlign: 'center', py: 2 }}><Box sx={{ fontSize: 15, fontWeight: 600 }}>Sending POT for signature…</Box></Box>}
      {phase === 'ok' && <Notice severity="success">{result.channel === 'owlrx'
        ? <>Sent to {words?.physician}'s OwlRx portal. The POT shows <b>Awaiting physician</b> until they sign.</>
        : <>Envelope {(result.envelope ?? '').slice(0, 8)}… sent to {name.trim()}, {email.trim()}.</>}</Notice>}
      {phase === 'blocked' && <Notice severity="info">{words?.blocked}</Notice>}
      {phase === 'fail' && <Notice severity="error">{result.error ?? 'The envelope could not be created.'}</Notice>}
    </AppDialog>
  )
}
